import LoginImage from '@assets/images/login.jpg';
import { LoadingScreen } from '@components/LoadingScreen';
import { LocaleToggle } from '@modules/application/components/LocaleToggle';
import { TenantBrandingHeader } from '@modules/tenants/components/TenantBrandingHeader';
import { TenantThemeContext } from '@modules/tenants/components/TenantThemeContextProvider';
import { Button, Container, Paper, Stack, TextField, Typography } from '@mui/material';
import { useGetMe } from '@utils/useGetMe';
import { useContext, useState } from 'react';
import { useIntl } from 'react-intl';
import { Link, useParams } from 'react-router-dom';
import { useNotification } from '../../../utils/useNotification';
import { useResponsive } from '@utils/useResponsive';

export const ForgotPasswordPage: React.FC = () => {
  const { formatMessage } = useIntl();
  const { tenantId } = useParams();
  const tenantContext = useContext(TenantThemeContext);
  const [email, setEmail] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { notifySuccess, notifyFailure } = useNotification();
  const { isFetching } = useGetMe();
  const { isMobile } = useResponsive();
  if (isFetching) return <LoadingScreen />;
  
  if (tenantId == null) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim()) return;
    setIsSubmitting(true);
    try {
      const formData = new FormData();
      formData.append('email', email.trim());
      const res = await fetch(`/${tenantId}/api/Auth/ForgotPassword`, {
        method: 'POST',
        body: formData,
        credentials: 'same-origin'
      });
      if (res.ok) {
        setSubmitted(true);
        notifySuccess(formatMessage({ id: 'If an account exists for this email, you will receive a reset link.' }));
      } else {
        notifyFailure(formatMessage({ id: 'An unexpected error has occurred' }));
      }
    } catch {
      notifyFailure(formatMessage({ id: 'An unexpected error has occurred' }));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Stack
      sx={{
        backgroundImage: `url(${ tenantContext.isBrandingEnabled ? tenantContext.loginBackgroundUrl ?? LoginImage : LoginImage})`,
        backgroundPosition: 'center center',
        backgroundRepeat: 'no-repeat',
        backgroundSize: 'cover',
        flexGrow: 1
      }}
      flex={1}
      justifyContent="center"
      alignItems="center"
    >
      <Container maxWidth="sm"  disableGutters sx={{ height: { xs: '100%', sm: 'auto' } }}> 
    <Paper elevation={isMobile ? 0 : 4}
    sx={{ 
      height: { xs: '100%', sm: 'auto' }, 
      width: { xs: '100%', sm: 'auto' },
    }}
    >
        <Stack p={4} gap={4} justifyContent="center" alignItems="center" width={'100%'}>
          <Stack direction="row" alignSelf="stretch" justifyContent="flex-end">
            <LocaleToggle />
          </Stack>
          <TenantBrandingHeader />
          <Typography variant="h5" color="primary.main">
            {formatMessage({ id: 'Reset password' })}
          </Typography>
          {submitted ? (
            <Typography textAlign="center">
              {formatMessage({ id: 'If an account exists for this email, you will receive a reset link.' })}
            </Typography>
          ) : (
            <Stack component="form" onSubmit={handleSubmit} spacing={2} width="100%">
              <TextField
                required
                fullWidth
                type="email"
                label={formatMessage({ id: 'Email' })}
                value={email}
                onChange={e => setEmail(e.target.value)}
                disabled={isSubmitting}
                autoComplete="email"
              />
              <Button type="submit" variant="contained" disabled={isSubmitting} fullWidth>
                {formatMessage({ id: 'Request password reset' })}
              </Button>
            </Stack>
          )}
          <Link to={`/${tenantId}/login`}>
            <Button variant="text" size="small">
              {formatMessage({ id: 'Go to login' })}
              </Button>
            </Link>
          </Stack>
        </Paper>
    </Container>
    </Stack>
  );
};
